import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ShieldCheck, Mic, Video, Check, Loader } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Inline permission gate that re-prompts a user who previously tapped
 * "Maybe Later" on /permissions. Renders as a small attention-grabbing card
 * above the call form. Once the browser reports the relevant permissions as
 * granted (or the user clicks Allow successfully) the card unmounts itself
 * via the onGranted callback.
 *
 * Props:
 *   - needVideo (bool): include camera in the request, default true
 *   - onGranted (fn):   parent should hide this card when called
 */
export const PermissionPromptCard = ({ needVideo = true, onGranted }) => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [status, setStatus] = useState('idle'); // idle | requesting | granted

  // On mount, check whether the browser already considers these permissions
  // granted (e.g. the user later flipped them on in browser settings). If so,
  // we silently dismiss without bothering the user.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        if (!navigator.permissions || !navigator.permissions.query) return;
        const mic = await navigator.permissions.query({ name: 'microphone' });
        let cam = { state: 'granted' };
        if (needVideo) {
          cam = await navigator.permissions.query({ name: 'camera' });
        }
        if (!cancelled && mic.state === 'granted' && cam.state === 'granted') {
          try { localStorage.setItem('tababa_perm_status', 'granted'); } catch (e) { /* ignore */ }
          onGranted && onGranted();
        }
      } catch (e) {
        // Some browsers (Safari) don't support the Permissions API for camera/mic.
        // We just keep showing the card and let the user opt in.
      }
    };
    check();
    return () => { cancelled = true; };
  }, [needVideo, onGranted]);

  const handleAllow = async () => {
    setStatus('requesting');
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('unsupported');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: needVideo,
      });
      // We only needed the permission grant — release the hardware immediately.
      stream.getTracks().forEach((t) => t.stop());
      setStatus('granted');
      try { localStorage.setItem('tababa_perm_status', 'granted'); } catch (e) { /* ignore */ }
      toast.success(t('تم منح الإذن بنجاح!', 'Permissions granted!', 'Autorisations accordées !'));
      // Give the user a beat to see the success state, then dismiss
      setTimeout(() => { onGranted && onGranted(); }, 600);
    } catch (e) {
      setStatus('idle');
      toast.error(t(
        'لم يتم منح الإذن. يمكنك تفعيله من إعدادات المتصفح.',
        'Permission not granted. You can enable it in browser settings.',
        "Autorisation refusée. Activez-la dans les paramètres."
      ));
    }
  };

  return (
    <Card
      className={`p-5 border-2 border-blue-200 bg-gradient-to-l from-blue-50 to-white shadow-sm ${isRTL ? 'rtl' : 'ltr'}`}
      data-testid="permission-prompt-card"
    >
      <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#1976d2] to-[#0d47a1] flex items-center justify-center shadow">
          <ShieldCheck size={22} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base md:text-lg font-extrabold text-[#0d47a1] mb-1" data-testid="permission-prompt-title">
            {needVideo
              ? t('السماح بالميكروفون والكاميرا', 'Allow Microphone & Camera', 'Autoriser micro & caméra')
              : t('السماح بالميكروفون', 'Allow Microphone', 'Autoriser le micro')}
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-3">
            {t(
              'تحتاج المكالمات الصحية إلى الميكروفون' + (needVideo ? ' والكاميرا' : '') + '. وافقت سابقاً على التذكير لاحقاً — الآن هو الوقت المناسب.',
              `Health consultations need access to your microphone${needVideo ? ' and camera' : ''}. You said "Maybe Later" before — now's the right time.`,
              `Les consultations nécessitent l'accès au micro${needVideo ? ' et à la caméra' : ''}. Vous avez choisi "Plus tard" — c'est le bon moment.`
            )}
          </p>

          <div className="flex flex-wrap gap-2 mb-3">
            <span className="inline-flex items-center gap-1 text-xs bg-white border border-blue-100 text-[#1565c0] px-2.5 py-1 rounded-full">
              <Mic size={12} /> {t('ميكروفون', 'Microphone', 'Micro')}
            </span>
            {needVideo && (
              <span className="inline-flex items-center gap-1 text-xs bg-white border border-blue-100 text-[#1565c0] px-2.5 py-1 rounded-full">
                <Video size={12} /> {t('كاميرا', 'Camera', 'Caméra')}
              </span>
            )}
          </div>

          <Button
            onClick={handleAllow}
            disabled={status === 'requesting'}
            className="bg-[#1565c0] hover:bg-[#0d47a1] rounded-xl font-bold h-10 px-5"
            data-testid="permission-prompt-allow"
          >
            {status === 'requesting' && <Loader className="animate-spin me-2" size={16} />}
            {status === 'granted' && <Check className="me-2" size={16} />}
            {status === 'requesting'
              ? t('جاري الطلب...', 'Requesting...', 'Demande...')
              : status === 'granted'
                ? t('تم!', 'Done!', 'OK !')
                : t('السماح الآن', 'Allow Now', 'Autoriser')}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default PermissionPromptCard;
